import type { LegacyWallVerticalDatumEntity } from "building-types/datums/datumEntityTypes";
import type {
  FacadeVerticalDatum,
  ResolvedBuildingDatum,
  WallAssemblyVerticalDatum,
} from "building-types/datums/datumsTypes";
import { VerticalJustification, WallVerticalDatumAnchor } from "building-types/datums/datumsTypesEnum";
import type { Level } from "building-types/levels/levelTypes";
import type { WallSideDescriptor } from "building-types/walls/wallDesciptorTypes";
import type { LevelInches, NominalNumber, WorldInches } from "common/types/nominalNumbersTypes";
import { assertUnreachable } from "common/utils/assertUnreachable";
import { levelInchesNew, levelInchesToWorld, nominalNumberNew, worldInchesNew } from "common/utils/nominalNumbers";
import { DIST_EPSILON } from "geom/core";
import { intersectRay3PlaneWithNormal } from "geom/intersectRay3";
import { planeNormal } from "geom/plane";
import type { Plane } from "geom/planeTypes";
import type { Vec2 } from "geom/vec2";
import { vec3Dot, vec3Perpendicular } from "geom/vec3";

export function getDatumZ(datum: ResolvedBuildingDatum | undefined, v: Vec2 = [0, 0]): WorldInches {
  if (!datum) {
    return worldInchesNew(0);
  }
  const { plane } = datum;

  return getPlaneZ(plane, v);
}

export function getPlaneZ(plane: Plane, [x, y]: Vec2 = [0, 0]): WorldInches {
  const [nx0, ny0, nz0] = planeNormal(plane);

  const dot = vec3Dot(nx0, ny0, nz0, 0, 0, 1);

  // test if plane is flat in XY plane
  if (Math.abs(Math.abs(dot) - 1) < DIST_EPSILON) {
    return worldInchesNew(plane.originZ);
  }

  // test if plane is perpendicular to Z axis
  if (vec3Perpendicular([nx0, ny0, nz0], [0, 0, 1], DIST_EPSILON)) {
    throw new Error("datum is perpendicular");
  }

  const res = intersectRay3PlaneWithNormal(
    x,
    y,
    Number.MIN_SAFE_INTEGER,
    0,
    0,
    1,
    plane.originX,
    plane.originY,
    plane.originZ,
    nx0,
    ny0,
    nz0,
  );

  if (!res) {
    throw new Error("datum is parallel");
  }

  const [, , z] = res;

  return worldInchesNew(z);
}

export function wallSideDatumToWorldZ(
  datum: LegacyWallVerticalDatumEntity,
  datumOffset: number,
  level: Level,
  wallSide: Pick<WallSideDescriptor, "wallSideDatums">,
): WorldInches {
  const offset = wallSideDatumToLevelOffset(datum, datumOffset, wallSide);
  return levelInchesToWorld(offset, level.floorElevation);
}

export function wallSideDatumToLevelOffset(
  datum: LegacyWallVerticalDatumEntity,
  datumOffset: number,
  wallSide: Pick<WallSideDescriptor, "wallSideDatums">,
): LevelInches {
  const { anchor } = datum;
  const { wallSideDatums } = wallSide;

  switch (anchor) {
    case WallVerticalDatumAnchor.BASE_WALL: {
      /**
       * @TODO - BASE_WALL should really be level.floorElevation to level.topOfWall for a consistent alignment
       * across a level.
       *
       * This setup will maintain current baselines on the existing houses,
       * by simulating the behavior that used to be attributed to the face generated at this wall side.
       * In legacy homes, the bottom of the face that was generated represented the max of floor, foundation and level,
       * hence the 0 here.
       *
       * We should consider a migration into LEGACY_BASE_WALL and not allow this behavior any longer.
       */
      const datumBottom = levelInchesNew(Math.max(wallSideDatums.floorTop, wallSideDatums.foundationTop, 0));
      const datumTop = levelInchesNew(wallSideDatums.ceilingBottom);
      return evaluateVerticalDatumOffset(datum, datumBottom, datumTop, datumOffset);
    }
    case WallVerticalDatumAnchor.GABLE_END: {
      /**
       * @TODO - find a way of attaching gable end datums to wall side datums. There
       * currently is no way of knowing what the gable end offset is relative to a wall side.
       *
       * Facades on walls with a GABLE_END datum currently always return a datum of ceilingTop.
       * In the event that this justification is BOTOM_UP - this is correct. However in the event that this
       * justification is TOP_DOWN, we do not have enough information to produce an accurate datum
       * from the top of the gable end. This needs to be tackled in a dedicated body of work.
       */
      // to avoid regressions
      if (wallSideDatums.roofBottom === wallSideDatums.roofTop) {
        return wallSideDatums.roofBottom;
      }

      const datumBottom = levelInchesNew(wallSideDatums.roofBottom);
      const datumTop = levelInchesNew(wallSideDatums.roofTop);
      return evaluateVerticalDatumOffset(datum, datumBottom, datumTop, datumOffset);
    }
    default: {
      assertUnreachable(anchor as never);
    }
  }
}

export function evaluateVerticalDatumOffset<T extends NominalNumber>(
  datum: Pick<WallAssemblyVerticalDatum | FacadeVerticalDatum, "justification" | "offset" | "offsetNormalized">,
  datumBottom: T,
  datumTop: T,
  datumOffset = 0,
) {
  const { offsetNormalized, offset = 0, justification } = datum;

  const datumHeight = datumTop - datumBottom;
  const offsetAdjusted = offsetNormalized ? datumHeight * offset : offset;

  let evaluatedOffset = 0;

  switch (justification) {
    case VerticalJustification.BOTTOM_UP: {
      evaluatedOffset = datumBottom + offsetAdjusted + datumOffset;
      break;
    }
    case VerticalJustification.TOP_DOWN: {
      evaluatedOffset = datumTop - offsetAdjusted + datumOffset;
      break;
    }
    default:
  }
  return nominalNumberNew<T>(evaluatedOffset);
}
